import React, { useEffect, useRef, useState } from "react";
import Icons from "../icons";
import * as Loader from "./Loader";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

export const TOKEN_HELP_URL = "https://id.atlassian.com/manage-profile/security/api-tokens";

/* ------------------------------------------------------------------ */
/*  Connect Jira                                                        */
/*                                                                     */
/*  One step: the email the user signs in to Jira with, and their own   */
/*  Jira API token. The server signs in to Jira with them, links the    */
/*  account Jira names (who Cortex names in Jira, what For you lists)   */
/*  and keeps the token encrypted: test runs are started, finished and  */
/*  filled in Jira with the user's own rights. The token is never shown */
/*  again. The email is not filled in for them: it is their JIRA email. */
/*  Used on the profile (inline) and on For you (in a dialog).          */
/* ------------------------------------------------------------------ */
export function JiraConnectForm({ userkey, onConnected, onCancel, autoFocus = false, idPrefix = "jira-connect" }) {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const emailRef = useRef(null);

  useEffect(() => {
    if (autoFocus) emailRef.current?.focus();
  }, [autoFocus]);

  const connect = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !token.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}for-you/jira-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userkey, email: email.trim(), token: token.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setToken("");
      onConnected?.(body?.data || null);
    } catch (err) {
      setError(err.message || "Could not connect to Jira.");
    } finally {
      setBusy(false);
    }
  };

  const fieldCls =
    "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
  return (
    <form onSubmit={connect} aria-label="Connect Jira" className="space-y-4">
      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-email`} className="text-sm font-medium text-slate-700">
          Jira email
        </label>
        <input
          id={`${idPrefix}-email`}
          ref={emailRef}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
          placeholder="the email you sign in to Jira with"
          className={fieldCls}
        />
      </div>
      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-token`} className="text-sm font-medium text-slate-700">
          Jira API token
        </label>
        <input
          id={`${idPrefix}-token`}
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="new-password"
          spellCheck={false}
          className={`${fieldCls} font-mono`}
        />
        <p className="text-xs text-slate-500">
          Create one at{" "}
          <a href={TOKEN_HELP_URL} target="_blank" rel="noreferrer" className="font-semibold text-blue-700 hover:underline">
            id.atlassian.com › Security › API tokens
          </a>
          . Cortex keeps it encrypted and never shows it again.
        </p>
      </div>
      {error && (
        <p role="alert" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-slate-300 bg-card px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={!email.trim() || !token.trim() || busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <Icons.Link className="h-4 w-4" />
          Connect Jira
        </button>
      </div>
      {busy && (
        <Loader.Overlay
          variant="guide"
          label="Connecting your Jira…"
          sublabel="Signing in to Jira with your email and token, and linking your account"
          delay={0}
        />
      )}
    </form>
  );
}

/* The form in a dialog (For you). `reason`: why an action asked for it. */
export default function JiraConnectDialog({ userkey, reason = "", onClose, onConnected }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={() => onClose?.()} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="jira-connect-title"
        className={`relative z-10 flex ${DIALOG_HEIGHT} w-full max-w-lg flex-col overflow-hidden rounded-xl bg-card shadow-2xl`}
      >
        <div className="flex flex-none items-start gap-3 border-b border-slate-200 px-5 py-4">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700">
            <Icons.Link className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="jira-connect-title" className="text-base font-semibold text-slate-900">
              Connect your Jira account
            </h2>
            <p className="mt-0.5 text-sm text-slate-600">
              Cortex lists what Jira assigns you, names you on what you make, and works your test runs in Jira with your
              own rights.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="cq-scroll min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {reason && (
            <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
              {reason}
            </p>
          )}
          <JiraConnectForm userkey={userkey} onConnected={onConnected} onCancel={onClose} autoFocus />
        </div>
      </div>
    </div>
  );
}
